import type { FunctionComponent } from 'react';
import React, { useMemo, useState, useCallback } from 'react';
import includes from 'lodash/includes';
import { useMutation } from '@apollo/client';
import Box from '@material-ui/core/Box';
import Alert from '@material-ui/lab/Alert';

import { getFirstName } from '@jerry-serverless/name-parser';

import { StepContainer } from '../components/step_container/StepContainer';
import { useStepManager } from '../steps';
import { guardedFunc } from '../utils/guarded_function';
import { trackEvent } from '../utils/analytics';
import { setSignupParams } from '../types/signup_params';
import { getClient, parseError } from '../apollo';
import type { UpdateUserData, UpdateUserInput } from '../apollo/graphql/update_user.mutation';
import { UPDATE_USER_MUTATION, SignupStep } from '../apollo/graphql/update_user.mutation';
import { mergeViewerQueryDataInAppData } from '../utils/app_data';
import { DisclaimerText } from '../components/disclaimer/DisclaimerText';
import { InfoBox } from '../components/info_box/InfoBox';
import { StepOptionList } from '../components/step_container/StepOptionList';
import { useIsomorphicLayoutEffect } from '../hooks/useIsomorphicLayoutEffect';

const STEP_NAME = 'askInsured';

interface InsuredOptionType {
  title: string;
  short?: string;
  isAutoInsured: boolean;
}

const INSURED_OPTIONS: InsuredOptionType[] = [
  {
    title: 'Yes, I have an active insurance policy',
    short: 'Yes, I am insured',
    isAutoInsured: true,
  },
  {
    title: 'No, I am not insured',
    isAutoInsured: false,
  },
];

/**
 * Ask if user is currently insured.
 */
const Insured: FunctionComponent<unknown> = () => {
  const {
    appData,
    direction,
    stepIdx,
    stepCnt,
    stepIndicatorData,
    goNext: origGoNext,
    canGoNext,
    goBack: origGoBack,
    canGoBack,
  } = useStepManager(STEP_NAME);
  const goNext = useMemo(() => guardedFunc(origGoNext, canGoNext), [origGoNext, canGoNext]);
  const goBack = useMemo(() => guardedFunc(origGoBack, canGoBack), [origGoBack, canGoBack]);
  const [isAutoInsured] = useState(
    // Do not prefill isAutoInsured. https://app.asana.com/0/1202292931304613/1208173814339812/f
    appData.signupParams?.partnerSignup
      ? appData.partnerSkippableData?.isAutoInsured
      : appData.signupParams?.isAutoInsured
  );

  const client = getClient();

  // Scroll to top of page
  useIsomorphicLayoutEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.scrollTo(0, 0);
  }, []);

  /**
   * Update user mutation to set selected insured prompt.
   */
  const [updateUser, updateUserMutObj] = useMutation<UpdateUserData, UpdateUserInput>(UPDATE_USER_MUTATION, { client });
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  /**
   * Note: Insured page can be shown before onboarding or during onboarding.
   */
  const onSelect = useCallback(
    async (isInsured: boolean) => {
      setErrorMsg(null);

      // If asked during onboarding, save to database and update 'appData'.
      if (appData.isSigned) {
        try {
          const resp = await updateUser({
            variables: {
              input: {
                isAutoInsured: isInsured,
                step: SignupStep.insured,
                autoInsuredLength: undefined,
              },
            },
          });

          // Send trackevents.
          void trackEvent('CreateAccountInsured', { eventLabel: isInsured ? 'Yes' : 'No' });
          void origGoNext(mergeViewerQueryDataInAppData(resp.data?.updateUser5.user || null, appData));
        } catch (err) {
          setErrorMsg(parseError(err));
        }
      } else {
        // Send trackevents.
        void trackEvent('CreateAccountInsured', { eventLabel: isInsured ? 'Yes' : 'No' });
        // If asked before onboarding, save to 'appData' directly
        void origGoNext({
          ...appData,
          signupParams: setSignupParams(appData.signupParams, {
            isAutoInsured: isInsured,
            autoInsuredLength: undefined,
          }),
        });
      }
    },
    [appData, origGoNext, updateUser]
  );

  /**
   * Check which field is selected.
   */
  const isSelected = useCallback(
    (option: InsuredOptionType): boolean => isAutoInsured === option.isAutoInsured,
    [isAutoInsured]
  );

  /**
   * Update subtitle of the page if page is shown during onboarding.
   */
  const insuredSubTitleText = useMemo(() => {
    if (appData.signupParams?.partnerSignup && appData.isSigned) {
      return `Hi ${getFirstName(appData.name || '') || 'there'}, lets find you savings on insurance.`;
    }
    return undefined;
  }, [appData]);

  const insuredCenterText = useMemo(
    () =>
      appData.selectedService?.refi
        ? 'Lenders require that you have current insurance to refinance your car loan.'
        : 'Insurers often ask for proof of your previous policy when you buy a new one. Help us get the right information now to get you an accurate rate.',
    [appData.selectedService?.refi]
  );
  const showDisclaimerText = includes(['google', 'googlebrand'], appData.utmSource);

  return (
    <StepContainer
      route={STEP_NAME}
      direction={direction}
      goNext={goNext}
      goBack={goBack}
      stepIdx={stepIdx}
      stepCnt={stepCnt}
      stepIndicatorData={stepIndicatorData}
      showLoader={updateUserMutObj.loading}
      isMobile={appData.isMobileAgent}
      title="Do you currently have a car insurance policy?"
      iconText={insuredSubTitleText}
    >
      <div>
        <Box width="80%" mx="auto">
          <StepOptionList
            options={INSURED_OPTIONS}
            optionToString={(option) => (appData.selectedService?.refi && option.short ? option.short : option.title)}
            selected={isSelected}
            onSelect={(option) => {
              void onSelect(option.isAutoInsured);
            }}
          />
        </Box>

        {errorMsg && (
          <Box mt={2}>
            <Alert severity="error">{errorMsg}</Alert>
          </Box>
        )}

        <InfoBox mt={4.75}>
          {!appData.selectedService?.refi && 'Heads up! '}
          {insuredCenterText}
        </InfoBox>
        {showDisclaimerText && <DisclaimerText />}
      </div>
    </StepContainer>
  );
};

export default Insured;
